<template>
  <div
    class="options"
    :class="{ 'satisfied': botPayload.question.questionId === 'are_you_satisfied' }">
    <ChatButton
      v-for="(option, index) in payload.question.options"
      :class="{selected: isActive === index || isActive === option.key}"
      :disabled="!isLastMessageInConversation || option.disabled"
      :key="option.key"
      :label="getUserLabels(option, botStatus)"
      @click="sendAnswer(option, index)"
    />
    <div
      v-if="botStatus === 'INITIALIZED'"
      class="init-question"
    >
      <a href="#" @click="talkWithAgent()" :class="{'disabled-href-link': !isLastMessageInConversation || backToAgent}"> {{ t('chatbot_talk_with_agent') }} </a>
    </div>
  </div>
</template>

<script>

import { mapActions, mapState, mapMutations, mapGetters } from 'vuex'
import ChatButton from '../ChatButton'
import { isEmpty, cloneDeep, pick } from 'lodash'
import { useI36n } from '@jota-one/i36n/src/index.vue2'

export default {
  name: 'ChatBotOptions',
  components: {
    ChatButton
  },
  props: ['botPayload', 'messageId', 'botStatus', 'answer'],

  setup () {
    const { $label: t } = useI36n()

    return { t }
  },

  data () {
    return {
      isActive: null,
      payload: null,
      backToAgent: false
    }
  },
  created () {
    this.payload = cloneDeep(this.botPayload)
    this.handleButtonStates(this.payload)
    this.isActive = this.answer

    if (this.chatbotAnsweredQuestions) {
      this.chatbotAnsweredQuestions.forEach(msg => {
        if (msg.msgId === this.messageId) {
          this.payload = cloneDeep(msg.botPayload)
          this.isActive = msg.botPayload.question.answer
        }
      })
    }
  },
  computed: {
    ...mapState('conversation', ['conversation', 'unreadMessages', 'getChatbotAnsweredQuestions']),
    ...mapGetters('conversation', {chatbotAnsweredQuestions: 'getChatbotAnsweredQuestions'}),
    isLastMessageInConversation () {
      let lastMessageIndex = this.conversation.messages.length - 1
      return this.conversation.messages[lastMessageIndex].msgId === this.messageId
    }
  },
  methods: {
    ...mapActions('conversation', ['sendMessage', 'openRating']),
    ...mapMutations('uistate', ['SET_TRANSCRIPT_STATE', 'SET_CONVERSATION_STATE']),
    ...mapMutations('conversation', ['ANSWER_QUESTION', 'ADD_CHATBOT_ANSWERED_QUESTION']),
    sendAnswer (answer, index) {
      if (answer.key === 'askAnotherQuestion') {
        this.askAnotherQuestion(answer)
      } else if (answer.key === 'endChat') {
        this.endChat(answer)
      } else {
        let requestContext = Object.assign({}, this.payload.context)
        if (isEmpty(requestContext.personSelectionsHistory)) {
          requestContext.personSelectionsHistory = []
        }
        if (this.payload.question.questionId !== 'are_you_satisfied') {
          requestContext.personSelectionsHistory.push(answer.key)
        }
        let chatBotAnswerPayload = {
          msgId: this.messageId,
          msgType: 'CHATBOT',
          content: answer.key,
          botPayload: {
            botId: this.payload.botId,
            context: requestContext,
            question: {
              questionId: this.payload.question.questionId,
              options: this.payload.question.options.map(option => pick(option, 'key', 'label', 'userLabel')),
              answer: answer.key
            }
          }
        }
        this.ADD_CHATBOT_ANSWERED_QUESTION(chatBotAnswerPayload)

        this.disableOptions()
        this.sendMessage(chatBotAnswerPayload)
      }
      this.backToAgent = true
      this.isActive = index
      this.ANSWER_QUESTION({msgId: this.messageId, answer: index})
    },
    disableOptions () {
      this.payload.question.options.forEach((option) => {
        option.disabled = true
      })
    },
    handleButtonStates (payload) {
      payload.question.options.forEach((option, index) => {
        option.userLabel = this.t('chatbot_coverageCheck', {username: option.label})
        if (payload.question.answer) {
          option.disabled = true
        }
        if (option.key === payload.question.answer) {
          this.isActive = index
          this.ANSWER_QUESTION({msgId: this.messageId, answer: index})
        }
      })
      if (payload.question.questionId === 'ask_another_question' && payload.question.answer === 'NO') {
        this.isActive = 'ask_another_question'
        this.ANSWER_QUESTION({msgId: this.messageId, answer: 'ask_another_question'})
      }
    },
    askAnotherQuestion (answer) {
      this.isActive = 'ask_another_question'
      this.ANSWER_QUESTION({msgId: this.messageId, answer: 'ask_another_question'})
      let chatBotAnswerPayload = {
        msgId: this.messageId,
        msgType: 'CHATBOT',
        content: answer.key,
        botStatus: 'TO_KUBE',
        botPayload: {
          botId: this.payload.botId,
          context: this.payload.context,
          question: {
            questionId: 'ask_another_question',
            options: this.payload.question.options,
            answer: answer.key
          }
        }
      }
      this.ADD_CHATBOT_ANSWERED_QUESTION(chatBotAnswerPayload)

      this.disableOptions()
      this.sendMessage(chatBotAnswerPayload)
    },
    endChat (answer) {
      let chatBotAnswerPayload = {
        msgId: this.messageId,
        msgType: 'CHATBOT',
        content: answer.key,
        botStatus: 'FINISHED',
        botPayload: {
          botId: this.payload.botId,
          context: this.payload.context,
          question: {
            questionId: 'ask_another_question',
            options: this.payload.question.options,
            answer: answer.key
          }
        }
      }
      this.ADD_CHATBOT_ANSWERED_QUESTION(chatBotAnswerPayload)

      this.sendMessage(chatBotAnswerPayload)
      this.isActive = 'end_chat'
      this.ANSWER_QUESTION({msgId: this.messageId, answer: 'end_chat'})
      this.disableOptions()
      this.$store.dispatch('uistate/continueForCustomer', null, {root: true})
    },
    talkWithAgent () {
      let chatBotAnswerPayload = {
        msgId: this.messageId,
        msgType: 'CHATBOT',
        content: 'NO',
        botStatus: this.botStatus,
        botPayload: {
          botId: this.payload.botId,
          context: this.payload.context,
          question: {
            questionId: 'pick_family_member',
            options: this.payload.question.options,
            answer: 'NO'
          }
        }
      }
      this.sendMessage(chatBotAnswerPayload)
    },
    getUserLabels (obj, status) {
      if (status === 'REMAINING_FAMILY_MEMBERS') {
        if (obj.key === 'askAnotherQuestion' || obj.key === 'endChat') {
          return obj.label
        } else {
          return obj.userLabel
        }
      } else {
        return obj.label
      }
    }
  }
}
</script>

<style scoped lang="postcss">
  .options {
    display: grid;
    padding-top: 10px;

    button {
      border-radius: 0;
      border: 1px solid black;
      width: 100%;
      background-color: white;

      &.selected {
        border: none;
        color: white;
        cursor: default;
        background-color: var(--tw-c-original-green);
      }

      &.disabled {
        cursor: default;
        background-color: white;
        color: var(--tw-c-dark-gray)
      }

      &.disabled.selected {
        color: white;
        cursor: default;
        background-color: var(--tw-c-original-green);
      }

      &.disabled:hover {
        pointer-events: none;
      }

      &.disabled:hover:not(.selected) {
        background-color: transparent;
        color: var(--tw-c-dark-gray);
      }
    }

    &.satisfied {
      display: block;
      text-align: left;

      button {
        border-radius: 20px 0 0 20px;
        border: 1px solid var(--tw-c-original-green);
        color: var(--tw-c-original-green);
        background-color: white;

        &.selected {
          border: 1px solid var(--tw-c-original-green);
          color: white;
          cursor: default;
          background-color: var(--tw-c-original-green);
        }

        &.disabled:hover {
           border: 1px solid var(--tw-c-original-green);
         }

        &:nth-child(2) {
          border-radius: 0 20px 20px 0;
        }

        &:nth-child(2).disabled:hover:not(.selected) {
          color: var(--tw-c-original-green);
        }
      }
    }

    .init-question {
      padding: 10px 0;
    }

    .disabled-href-link {
      pointer-events: none;
      cursor: default;
    }
  }
</style>
